import { useRef, useState, useEffect, useCallback } from "react";

function useFakeScrollbar(dependencies = []) {
	const containerRef = useRef(null);
	const dragStateRef = useRef({
		isDragging: false,
		startX: 0,
		startThumbLeft: 0,
	});
	const [thumbWidth, setThumbWidth] = useState(0);
	const [thumbLeft, setThumbLeft] = useState(0);
	const [showScrollbar, setShowScrollbar] = useState(false);

	// Use useCallback to maintain reference equality between renders
	const updateScrollbar = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		const scrollWidth = container.scrollWidth;
		const clientWidth = container.clientWidth;
		const scrollLeft = container.scrollLeft;

		if (scrollWidth > clientWidth) {
			setShowScrollbar(true);
			const newThumbWidth = (clientWidth / scrollWidth) * clientWidth;
			const maxScrollLeft = scrollWidth - clientWidth;
			const maxThumbLeft = clientWidth - newThumbWidth;
			const newThumbLeft =
				maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeft : 0;
			setThumbWidth(newThumbWidth);
			setThumbLeft(newThumbLeft);
		} else {
			setShowScrollbar(false);
		}
	}, []);

	// Handle pointer movement with useCallback
	const handlePointerMove = useCallback((e) => {
		if (!dragStateRef.current.isDragging) return;

		const container = containerRef.current;
		if (!container) return;

		const clientWidth = container.clientWidth;
		const scrollWidth = container.scrollWidth;
		const computedThumbWidth = (clientWidth / scrollWidth) * clientWidth;
		const maxThumbLeft = clientWidth - computedThumbWidth;
		const maxScrollLeft = scrollWidth - clientWidth;

		const deltaX = e.clientX - dragStateRef.current.startX;
		let newThumbLeft = dragStateRef.current.startThumbLeft + deltaX;
		newThumbLeft = Math.max(0, Math.min(newThumbLeft, maxThumbLeft));

		container.scrollLeft = (newThumbLeft / maxThumbLeft) * maxScrollLeft;
	}, []);

	// Handle pointer up with useCallback
	const handlePointerUp = useCallback(() => {
		dragStateRef.current.isDragging = false;
		window.removeEventListener("pointermove", handlePointerMove);
		window.removeEventListener("pointerup", handlePointerUp);
	}, [handlePointerMove]);

	// Handle thumb pointer down with useCallback
	const handleThumbPointerDown = useCallback(
		(e) => {
			e.preventDefault();
			const container = containerRef.current;
			if (!container) return;

			dragStateRef.current.isDragging = true;
			dragStateRef.current.startX = e.clientX;
			dragStateRef.current.startThumbLeft = thumbLeft;

			window.addEventListener("pointermove", handlePointerMove);
			window.addEventListener("pointerup", handlePointerUp);
		},
		[handlePointerMove, handlePointerUp, thumbLeft],
	);

	// Handle keyboard navigation
	const handleKeyDown = useCallback(
		(e) => {
			const container = containerRef.current;
			if (!container || !showScrollbar) return;

			const scrollAmount = 40; // Adjust as needed

			switch (e.key) {
				case "ArrowLeft":
					container.scrollLeft -= scrollAmount;
					break;
				case "ArrowRight":
					container.scrollLeft += scrollAmount;
					break;
				default:
					return;
			}

			e.preventDefault();
		},
		[showScrollbar],
	);

	// First useEffect for initial setup and cleanup
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		// Set up event listeners
		container.addEventListener("scroll", updateScrollbar);
		window.addEventListener("resize", updateScrollbar);
		container.addEventListener("keydown", handleKeyDown);

		// Clean up all event listeners
		return () => {
			container.removeEventListener("scroll", updateScrollbar);
			window.removeEventListener("resize", updateScrollbar);
			window.removeEventListener("pointermove", handlePointerMove);
			window.removeEventListener("pointerup", handlePointerUp);
			container.removeEventListener("keydown", handleKeyDown);
		};
	}, [updateScrollbar, handlePointerMove, handlePointerUp, handleKeyDown]);

	// Second useEffect specifically for updating the scrollbar when dependencies change
	useEffect(() => {
		// Small delay to ensure the DOM has updated with new tableData
		const timeoutId = setTimeout(() => {
			updateScrollbar();
		}, 0);

		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [...dependencies, updateScrollbar]);

	return {
		containerRef,
		thumbWidth,
		thumbLeft,
		showScrollbar,
		handleThumbPointerDown,
		handleKeyDown,
		updateScrollbar, // Export updateScrollbar for manual updates
	};
}

export default useFakeScrollbar;